import React, {useState, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Table,
  Modal,
  Form,
  Badge,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaUsers,
  FaCheckCircle,
  FaSearch,
} from "react-icons/fa";
import allCampaigns from "../data/campaigns.json"; // TODO: This should come from props
import allLists from "../data/broadcastLists.json"; // TODO: This should come from props

// Componente reutilizable para las tarjetas de KPIs
const KpiCard = ({icon, title, value, color}) => (
  <Card
    className={`shadow-sm rounded-4 border-0 border-start border-5 border-${color} h-100`}
  >
    <Card.Body className="d-flex align-items-center">
      <div className={`fs-2 text-${color} me-3`}>{icon}</div>
      <div>
        <div className="fs-4 fw-bold">{value}</div>
        <div className="text-muted text-uppercase small">{title}</div>
      </div>
    </Card.Body>
  </Card>
);

const Organizations = ({organizations, setOrganizations, contacts}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [formState, setFormState] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrganization(null);
  };

  const handleShowCreateModal = () => {
    setFormState({
      name: "",
      shortName: "",
      rut: "",
      phone: "",
      contactEmail: "",
      contactPerson: "",
      distributionCenter: "",
      status: "Activo",
    });
    setEditingOrganization(null);
    setShowModal(true);
  };

  const handleShowEditModal = (organization) => {
    setEditingOrganization(organization);
    setFormState(organization);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const {name, value, checked} = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: name === "status" ? (checked ? "Activo" : "Inactivo") : value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (editingOrganization) {
      // Actualizar
      setOrganizations(
        organizations.map((org) =>
          org.id === editingOrganization.id ? formState : org
        )
      );
    } else {
      // Crear
      const newOrganization = {
        id: `trans-${Date.now()}`,
        ...formState,
      };
      setOrganizations([...organizations, newOrganization]);
    }
    handleCloseModal();
  };

  // --- Cálculo de Métricas por Organización ---
  const organizationMetrics = useMemo(() => {
    const metrics = {};
    (organizations || []).forEach((org) => {
      const associatedContacts = contacts.filter(
        (c) => c.transportista === org.name
      );
      const contactIds = new Set(associatedContacts.map((c) => c.id));

      const campaignsSent = allCampaigns.filter((campaign) =>
        (campaign.sentToListIds || []).some((listId) => {
          const list = allLists.find((l) => l.id === listId);
          return list?.members.some((memberId) => contactIds.has(memberId));
        })
      );

      metrics[org.id] = {
        contactCount: associatedContacts.length,
        campaignCount: campaignsSent.length,
      };
    });
    return metrics;
  }, [organizations, contacts]);

  // --- Lógica de Búsqueda y Paginación ---
  const filteredOrganizations = useMemo(() => {
    return (organizations || []).filter((t) =>
      `${t.name} ${t.rut} ${t.contactPerson}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [organizations, searchTerm]);

  // Efecto para reiniciar la paginación al buscar
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrganizations.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, currentPage + halfPagesToShow);

    if (currentPage - halfPagesToShow <= 1) {
      endPage = Math.min(totalPages, maxPagesToShow);
    }

    if (currentPage + halfPagesToShow >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => paginate(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => paginate(1)}>
          {1}
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => paginate(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => paginate(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Encabezado de Organizaciones */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="fw-bold mb-1">
                <FaBuilding className="me-3 text-primary" />
                Gestión de Organizaciones
              </h2>
              <p className="text-muted mb-0">
                Administra las empresas y organizaciones de tu red
              </p>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleShowCreateModal}
              className="rounded-pill px-4"
            >
              <FaPlus className="me-2" />
              Nueva Organización
            </Button>
          </div>
        </Col>
      </Row>

      {/* Fila de KPIs */}
      <Row className="mb-4">
        <Col md={4}>
          <KpiCard
            icon={<FaBuilding />}
            title="Organizaciones Totales"
            value={organizations.length}
            color="primary"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaUsers />}
            title="Contactos Asociados"
            value={contacts.length}
            color="info"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaCheckCircle />}
            title="Organizaciones Activas"
            value={organizations.filter((t) => t.status === "Activo").length}
            color="success"
          />
        </Col>
      </Row>

      <Card className="shadow-sm rounded-4 border-0">
        <Card.Header className="bg-white p-3 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">
            <FaBuilding className="me-2 text-primary" />
            Todas las Organizaciones
          </h5>
          <InputGroup style={{width: "300px"}}>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar por nombre o RUT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Header>
        <Card.Body>
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Nombre de la Organización</th>
                <th>RUT</th>
                <th>Nro. Contacto</th>
                <th>Contacto Principal</th>
                <th className="text-center">Contactos</th>
                <th className="text-center">Campañas</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((org) => (
                <tr key={org.id}>
                  <td className="fw-bold">{org.name}</td>
                  <td>{org.rut}</td>
                  <td>{org.phone}</td>
                  <td>{org.contactPerson}</td>
                  <td className="text-center">
                    {organizationMetrics[org.id]?.contactCount || 0}
                  </td>
                  <td className="text-center">
                    {organizationMetrics[org.id]?.campaignCount || 0}
                  </td>
                  <td className="text-center">
                    {org.status === "Activo" ? (
                      <Badge bg="success">Activo</Badge>
                    ) : (
                      <Badge bg="danger">Inactivo</Badge>
                    )}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      title="Editar"
                      onClick={() => handleShowEditModal(org)}
                    >
                      <FaEdit />
                    </Button>
                    {(organizationMetrics[org.id]?.campaignCount || 0) ===
                      0 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Eliminar"
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-center">
            <Pagination>{renderPaginationItems()}</Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* Modal para Crear/Editar */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingOrganization
                ? "Editar Organización"
                : "Añadir Organización"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la Organización</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Ej: Transportes Rápidos S.A."
                value={formState.name || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nombre Corto (Opcional)</Form.Label>
              <Form.Control
                type="text"
                name="shortName"
                placeholder="Ej: TRSA"
                value={formState.shortName || ""}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>RUT</Form.Label>
              <Form.Control
                type="text"
                name="rut"
                placeholder="76.123.456-7"
                value={formState.rut || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contacto Principal</Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                placeholder="Nombre del contacto"
                value={formState.contactPerson || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email de Contacto</Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                placeholder="contacto@empresa.com"
                value={formState.contactEmail || ""}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Número de Contacto</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                placeholder="+5622..."
                value={formState.phone || ""}
                onChange={handleFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Centro de Distribución (Opcional)</Form.Label>
              <Form.Control
                type="text"
                name="distributionCenter"
                placeholder="Ej: CD Lo Aguirre"
                value={formState.distributionCenter || ""}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="status-switch"
              name="status"
              label="Empresa Activa"
              checked={formState.status === "Activo"}
              onChange={handleFormChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Organizations;
